import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { orderedCategoryTasks, moveWithinCompletionGroup } from '../utils/taskOrdering';
import {
  subscribeToTasks,
  addTask as fbAddTask,
  updateTask as fbUpdateTask,
  deleteTask as fbDeleteTask,
  batchUpdateTasks,
  batchDeleteTasks,
  moveDueTasksToToday,
  createPostRequest,
  getAppDate,
  updateAppDate,
  subscribeToAppDate,
  subscribeToRecurringTasks,
  addRecurringTask as fbAddRecurringTask,
  updateRecurringTask as fbUpdateRecurringTask,
  deleteRecurringTask as fbDeleteRecurringTask,
  generateDueRecurringTasks,
  migrateDailyTasksToRecurring,
  recurringMatchesDate,
  subscribeToBucketItems,
  addBucketItem as fbAddBucketItem,
  updateBucketItem as fbUpdateBucketItem,
  deleteBucketItem as fbDeleteBucketItem,
  completeBucketItem as fbCompleteBucketItem,
  restoreBucketItem as fbRestoreBucketItem,
  setBirthdate as fbSetBirthdate,
  subscribeToBirthdate,
} from '../services/firebase';
import {
  cacheAllTasks,
  getCachedTasks,
  cacheSingleTask,
  removeCachedTask,
  addPendingOp,
  getPendingOps,
  clearPendingOps,
  isOnline,
} from '../services/offlineDb';

const TaskContext = createContext(null);

export const useTask = () => useContext(TaskContext);

// ローカル日付をYYYY-MM-DD文字列に変換（タイムゾーン安全）
function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// アプリ日付の翌日(YYYY-MM-DD)を返す（タイムゾーン安全）
// アプリ日付を唯一の真実とするため、実カレンダー(new Date())は一切使わない。
// 実時刻が0時を跨いでいても、必ず「アプリ日付 + 1日」を返す。
function nextAppDate(currentAppDate) {
  const parts = currentAppDate.split('-');
  const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  d.setDate(d.getDate() + 1);
  return toDateStr(d);
}

export function TaskProvider({ children }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [bucketItems, setBucketItems] = useState([]);
  const [birthdate, setBirthdateState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  // 初期値null: Firebaseから取得するまで日付を確定しない（0時跨ぎで勝手に更新されない）
  const [appDate, setAppDate] = useState(null);
  // リスト間移動の「元に戻す」トースト（{toLabel, undoInfo} | null）
  const [moveToast, setMoveToast] = useState(null);
  const toastTimerRef = useRef(null);
  // バケットリスト達成おめでとうポップアップ（{name} | null。3秒で自動消失）
  const [achievement, setAchievement] = useState(null);
  const achievementTimerRef = useRef(null);

  // アンマウント時にトーストのタイマーを破棄
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // オンライン状態の監視
  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      syncPendingOps();
    };
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // リアルタイムリスナーでタスクを同期（スマホ・PC間共有）
  useEffect(() => {
    if (!user) {
      setTasks([]);
      return;
    }

    setLoading(true);

    // アプリ日付を取得 → 旧毎日タスクの移行 → future移動 → 定期タスクの当日分生成
    getAppDate(user.uid).then(async (date) => {
      setAppDate(date);
      // 日付の意思決定は常にアプリ日付(date)基準。
      // 実カレンダー(new Date())は使わない＝実時刻が0時を跨いでも
      // アプリ日付がまだ前日なら、前日の定期タスク・future移動のままになる。
      try {
        // 旧仕様の「毎日やるタスク」を定期タスク(毎日)へ引っ越し（あれば）
        await migrateDailyTasksToRecurring(user.uid);
        await moveDueTasksToToday(user.uid, date);
        // アプリ日付に該当する定期タスクを今日やることへ自動追加
        await generateDueRecurringTasks(user.uid, date);
      } catch (err) {
        console.error('Startup task generation failed:', err);
      }
    });

    // アプリ日付のリアルタイムリスナー
    const unsubDate = subscribeToAppDate(user.uid, (date) => {
      setAppDate(date);
    });

    // 定期タスク定義のリアルタイムリスナー（端末間同期）
    const unsubRecurring = subscribeToRecurringTasks(user.uid, (data) => {
      setRecurringTasks(data);
    });

    // バケットリストのリアルタイムリスナー
    const unsubBucket = subscribeToBucketItems(user.uid, (data) => {
      setBucketItems(data);
    });

    // 生年月日のリアルタイムリスナー（settings/appState.birthdate）
    const unsubBirthdate = subscribeToBirthdate(user.uid, (bd) => {
      setBirthdateState(bd);
    });

    if (isOnline()) {
      // リアルタイムリスナーを使用（端末間で即座に同期）
      const unsubscribe = subscribeToTasks(user.uid, (data) => {
        setTasks(data);
        cacheAllTasks(data);
        setLoading(false);
      });
      return () => {
        unsubscribe();
        unsubDate();
        unsubRecurring();
        unsubBucket();
        unsubBirthdate();
      };
    } else {
      // オフライン時はキャッシュから読み込み
      getCachedTasks().then((cached) => {
        setTasks(cached);
        setLoading(false);
      });
      return () => {
        unsubDate();
        unsubRecurring();
        unsubBucket();
        unsubBirthdate();
      };
    }
  }, [user]);

  // 保留操作の同期
  const syncPendingOps = async () => {
    if (!user || !isOnline()) return;
    const ops = await getPendingOps();
    if (ops.length === 0) return;

    for (const op of ops) {
      try {
        switch (op.type) {
          case 'add':
            await fbAddTask(user.uid, op.data);
            break;
          case 'update':
            await fbUpdateTask(user.uid, op.taskId, op.data);
            break;
          case 'delete':
            await fbDeleteTask(user.uid, op.taskId);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('Sync op failed:', err);
      }
    }
    await clearPendingOps();
  };

  // タスク追加
  const addNewTask = async (title, category, date = null) => {
    const categoryTasks = tasks.filter((t) => t.category === category);
    const maxOrder = categoryTasks.length > 0
      ? Math.max(...categoryTasks.map((t) => t.order || 0))
      : 0;

    const taskData = {
      title,
      category,
      completed: false,
      date,
      order: maxOrder + 1,
    };

    if (isOnline() && user) {
      try {
        await fbAddTask(user.uid, taskData);
        // リアルタイムリスナーが自動で反映するのでsetTasks不要
      } catch (err) {
        console.error('Add task failed:', err);
      }
    } else {
      const tempId = 'temp_' + Date.now();
      const newTask = { id: tempId, ...taskData, createdAt: new Date(), updatedAt: new Date() };
      setTasks((prev) => [...prev, newTask]);
      await cacheSingleTask(newTask);
      await addPendingOp({ type: 'add', data: taskData });
    }
  };

  // タスク更新
  const updateTaskById = async (taskId, updates) => {
    // オプティミスティック更新
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
    );

    if (isOnline() && user) {
      try {
        await fbUpdateTask(user.uid, taskId, updates);
      } catch (err) {
        console.error('Update task failed:', err);
      }
    } else {
      const updatedTask = tasks.find((t) => t.id === taskId);
      if (updatedTask) {
        await cacheSingleTask({ ...updatedTask, ...updates });
      }
      await addPendingOp({ type: 'update', taskId, data: updates });
    }
  };

  // タスク削除
  const deleteTaskById = async (taskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    await removeCachedTask(taskId);

    if (isOnline() && user) {
      try {
        await fbDeleteTask(user.uid, taskId);
      } catch (err) {
        console.error('Delete task failed:', err);
      }
    } else {
      await addPendingOp({ type: 'delete', taskId });
    }
  };

  // 並び替え
  const reorderTasks = async (category, oldIndex, newIndex) => {
    const categoryTasks = moveWithinCompletionGroup(
      orderedCategoryTasks(tasks, category), oldIndex, newIndex
    );
    if (!categoryTasks) return;

    const updates = categoryTasks.map((t, i) => ({
      id: t.id,
      data: { order: i },
    }));

    const updatedTasks = tasks.map((t) => {
      const update = updates.find((u) => u.id === t.id);
      return update ? { ...t, order: update.data.order } : t;
    });
    setTasks(updatedTasks);
    await cacheAllTasks(updatedTasks);

    if (isOnline() && user) {
      try {
        await batchUpdateTasks(user.uid, updates);
      } catch (err) {
        console.error('Reorder failed:', err);
      }
    }
  };

  // 今日のタスクを終了する処理
  // selectedTomorrowIds: ユーザーが明日やることに選んだタスクID
  const finishDay = async (freeText, shouldPost = false, selectedTomorrowIds = []) => {
    if (!user) return;

    try {
      const completedToday = tasks.filter(
        (t) => t.category === 'today' && t.completed
      );
      const completedFuture = tasks.filter(
        (t) => t.category === 'future' && t.completed
      );

      // 完了済み今日タスク＋完了済み明日以降タスクを削除
      const completedTodayIds = completedToday.map((t) => t.id);
      const completedFutureIds = completedFuture.map((t) => t.id);
      // 定期タスク由来の今日タスクは完了/未完了に関わらず削除（次の該当日に再生成される）
      const recurringTodayIds = tasks
        .filter((t) => t.category === 'today' && t.recurringId)
        .map((t) => t.id);
      const allDeleteIds = [
        ...new Set([...completedTodayIds, ...completedFutureIds, ...recurringTodayIds]),
      ];

      if (isOnline()) {
        try {
          if (allDeleteIds.length > 0) {
            await batchDeleteTasks(user.uid, allDeleteIds);
          }
        } catch (err) {
          console.error('Finish day batch ops failed:', err);
        }
      }

      // アプリ日付を翌日へ進める（必ずアプリ日付+1日。実時刻は使わない）
      const baseAppDate = appDate || toDateStr(new Date());
      const newAppDate = nextAppDate(baseAppDate);
      try {
        await updateAppDate(user.uid, newAppDate);
      } catch (err) {
        console.error('Update app date failed:', err);
      }
      setAppDate(newAppDate);

      // futureタスクを今日に移動
      // - 日付ベース：date <= newAppDate のfutureタスクを自動移動
      // - 手動選択：selectedTomorrowIdsに含まれるfutureタスクも強制移動（日付無視）
      //   ※todayカテゴリの未完了タスクは残留なのでID渡しても無影響
      try {
        await moveDueTasksToToday(user.uid, newAppDate, selectedTomorrowIds);
        // 新しい日付に該当する定期タスクを今日やることへ自動追加
        await generateDueRecurringTasks(user.uid, newAppDate);
      } catch (err) {
        console.error('Move due tasks failed:', err);
      }

      // ローカル状態を更新（リアルタイムリスナーでも反映されるが即座に）
      setTasks((prev) =>
        prev
          .filter((t) => !allDeleteIds.includes(t.id))
          .map((t) => {
            // 手動選択されたfutureタスクをtoday扱いに即時反映
            if (t.category === 'future' && selectedTomorrowIds.includes(t.id)) {
              return { ...t, category: 'today', date: null };
            }
            return t;
          })
      );
    } catch (err) {
      console.error('finishDay unexpected error:', err);
    }
  };

  // カテゴリ別タスク取得
  const getTasksByCategory = (category) => orderedCategoryTasks(tasks, category);

  // 手動で日付を変更
  const setAppDateManually = async (newDate) => {
    if (!user) return;
    setAppDate(newDate);
    await updateAppDate(user.uid, newDate);
  };

  // ===== 定期タスク（recurringTasks）=====

  // 定期タスクの追加。該当日なら即座に今日やることへ反映
  const addRecurring = async (rec) => {
    if (!user) return;
    const maxOrder = recurringTasks.length > 0
      ? Math.max(...recurringTasks.map((r) => r.order || 0))
      : 0;
    try {
      await fbAddRecurringTask(user.uid, { ...rec, order: maxOrder + 1 });
      const baseDate = appDate || toDateStr(new Date());
      await generateDueRecurringTasks(user.uid, baseDate);
    } catch (err) {
      console.error('Add recurring failed:', err);
    }
  };

  // 定期タスクの更新。内容変更時は同日再生成を許可するためlastGeneratedDateをリセット
  const updateRecurring = async (recId, updates) => {
    if (!user) return;
    try {
      await fbUpdateRecurringTask(user.uid, recId, { ...updates, lastGeneratedDate: null });
      const baseDate = appDate || toDateStr(new Date());
      await generateDueRecurringTasks(user.uid, baseDate);
    } catch (err) {
      console.error('Update recurring failed:', err);
    }
  };

  // 定期タスクの削除。既に今日へ生成済みのインスタンスも合わせて削除
  const deleteRecurring = async (recId) => {
    if (!user) return;
    try {
      await fbDeleteRecurringTask(user.uid, recId);
      const orphanIds = tasks
        .filter((t) => t.category === 'today' && t.recurringId === recId)
        .map((t) => t.id);
      if (orphanIds.length > 0) {
        await batchDeleteTasks(user.uid, orphanIds);
        setTasks((prev) => prev.filter((t) => !orphanIds.includes(t.id)));
      }
    } catch (err) {
      console.error('Delete recurring failed:', err);
    }
  };

  // 指定日に該当する定期タスク定義を取得（日報の「明日やること」表示用など）
  const getRecurringDueOn = (dateStr) =>
    recurringTasks.filter((r) => recurringMatchesDate(r, dateStr));

  // ===== タスクのリスト間移動（長押し）＋元に戻す =====

  // 今日⇔明日以降の移動。移動後3秒間「元に戻す」トーストを表示する
  const moveTaskToOpposite = (task) => {
    if (!task || (task.category !== 'today' && task.category !== 'future')) return;
    const toCategory = task.category === 'today' ? 'future' : 'today';

    const targetTasks = tasks.filter((t) => t.category === toCategory);
    const maxOrder = targetTasks.length > 0
      ? Math.max(...targetTasks.map((t) => t.order || 0))
      : 0;

    const updates = {
      category: toCategory,
      date: null,
      order: maxOrder + 1,
    };
    // 定期タスク由来のタスクを明日以降へ動かした場合は普通のタスクに変える
    // （翌日の自動生成と重複しないようにするため）
    if (task.recurringId && toCategory === 'future') {
      updates.recurringId = null;
      updates.recurLabel = null;
    }

    // 元に戻すための情報を保存
    const undoInfo = {
      taskId: task.id,
      data: {
        category: task.category,
        date: task.date || null,
        order: task.order || 0,
        recurringId: task.recurringId || null,
        recurLabel: task.recurLabel || null,
      },
    };

    updateTaskById(task.id, updates);

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setMoveToast({
      toLabel: toCategory === 'future' ? '明日以降' : '今日',
      undoInfo,
    });
    toastTimerRef.current = setTimeout(() => setMoveToast(null), 3000);
  };

  // ===== バケットリスト =====

  const addBucket = async ({ name, targetYears, targetMonths }) => {
    if (!user) return;
    const maxOrder = bucketItems.length > 0
      ? Math.max(...bucketItems.map((b) => b.order || 0))
      : 0;
    try {
      await fbAddBucketItem(user.uid, {
        name,
        targetYears: Number(targetYears) || 0,
        targetMonths: Number(targetMonths) || 0,
        order: maxOrder + 1,
      });
    } catch (err) {
      console.error('Add bucket failed:', err);
    }
  };

  const updateBucket = async (itemId, updates) => {
    if (!user) return;
    try {
      await fbUpdateBucketItem(user.uid, itemId, updates);
    } catch (err) {
      console.error('Update bucket failed:', err);
    }
  };

  const deleteBucket = async (itemId) => {
    if (!user) return;
    try {
      await fbDeleteBucketItem(user.uid, itemId);
    } catch (err) {
      console.error('Delete bucket failed:', err);
    }
  };

  const completeBucket = async (itemId) => {
    if (!user) return;
    // 達成おめでとうポップアップを表示（3秒で自動消失）。
    // 楽観的に先に出して、書き込み失敗時のみ消す。
    const target = bucketItems.find((b) => b.id === itemId);
    setAchievement({ name: target ? target.name : '' });
    if (achievementTimerRef.current) clearTimeout(achievementTimerRef.current);
    achievementTimerRef.current = setTimeout(() => {
      setAchievement(null);
      achievementTimerRef.current = null;
    }, 3000);
    try {
      await fbCompleteBucketItem(user.uid, itemId);
    } catch (err) {
      console.error('Complete bucket failed:', err);
      setAchievement(null);
      if (achievementTimerRef.current) {
        clearTimeout(achievementTimerRef.current);
        achievementTimerRef.current = null;
      }
    }
  };

  const restoreBucket = async (itemId) => {
    if (!user) return;
    try {
      await fbRestoreBucketItem(user.uid, itemId);
    } catch (err) {
      console.error('Restore bucket failed:', err);
    }
  };

  const setBirthdate = async (newBirthdate) => {
    if (!user) return;
    setBirthdateState(newBirthdate);
    try {
      await fbSetBirthdate(user.uid, newBirthdate);
    } catch (err) {
      console.error('Set birthdate failed:', err);
    }
  };

  // 直前の移動を取り消す
  const undoMove = () => {
    if (!moveToast) return;
    const { taskId, data } = moveToast.undoInfo;
    updateTaskById(taskId, data);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setMoveToast(null);
  };

  return (
    <TaskContext.Provider
      value={{
        tasks,
        recurringTasks,
        loading,
        online,
        appDate,
        addNewTask,
        updateTaskById,
        deleteTaskById,
        reorderTasks,
        finishDay,
        getTasksByCategory,
        setAppDateManually,
        addRecurring,
        updateRecurring,
        deleteRecurring,
        getRecurringDueOn,
        moveTaskToOpposite,
        undoMove,
        moveToast,
        // バケットリスト
        bucketItems,
        birthdate,
        addBucket,
        updateBucket,
        deleteBucket,
        completeBucket,
        restoreBucket,
        setBirthdate,
        achievement,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
}
